import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { ProjectFormModal } from '../projects/ProjectFormModal';
import { TaskFormModal } from '../tasks/TaskFormModal';
import {
  AccountMeetingMinutesPanel,
  type MeetingMinuteSelection,
} from './AccountMeetingMinutesPanel';
import type { Client, Project, Task } from '../../types/crm';

export type MeetingMinutesModalProps = {
  open: boolean;
  onClose: () => void;
  client: Client;
  canEdit: boolean;
  initialMinuteId?: string | null;
};

export function MeetingMinutesModal({
  open,
  onClose,
  client,
  canEdit,
  initialMinuteId = null,
}: MeetingMinutesModalProps) {
  const [selection, setSelection] = useState<MeetingMinuteSelection>({ id: null, isNew: true });
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectPrefillTitle, setProjectPrefillTitle] = useState<string | undefined>();
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskPrefillTitle, setTaskPrefillTitle] = useState<string | undefined>();
  const [taskPrefillDescription, setTaskPrefillDescription] = useState<string | undefined>();
  const [taskMeetingMinuteId, setTaskMeetingMinuteId] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    if (initialMinuteId) {
      setSelection({ id: initialMinuteId, isNew: false });
    } else {
      setSelection({ id: null, isNew: true });
    }
  }, [open, initialMinuteId, client.id]);

  return (
    <>
      <Modal
        title={`Meeting minutes · ${client.company_name}`}
        open={open}
        onClose={onClose}
        extraWide
        panelClassName="modal-panel--minutes"
      >
        <AccountMeetingMinutesPanel
          client={client}
          canEdit={canEdit}
          selection={selection}
          onSelectionChange={setSelection}
          onAddProject={({ title }) => {
            setProjectPrefillTitle(title);
            setEditingProject(null);
            setProjectModalOpen(true);
          }}
          onAddTask={({ title, description, meetingMinuteId }) => {
            setTaskPrefillTitle(title);
            setTaskPrefillDescription(description);
            setTaskMeetingMinuteId(meetingMinuteId);
            setEditingTask(null);
            setTaskModalOpen(true);
          }}
        />
      </Modal>

      <ProjectFormModal
        open={projectModalOpen}
        project={editingProject}
        defaultClientId={client.id}
        defaultTitle={projectPrefillTitle}
        lockClient
        onClose={() => {
          setProjectModalOpen(false);
          setEditingProject(null);
          setProjectPrefillTitle(undefined);
        }}
      />

      <TaskFormModal
        open={taskModalOpen}
        task={editingTask}
        defaultClientId={client.id}
        defaultTitle={taskPrefillTitle}
        defaultDescription={taskPrefillDescription}
        defaultMeetingMinuteId={taskMeetingMinuteId}
        lockClient
        onClose={() => {
          setTaskModalOpen(false);
          setEditingTask(null);
          setTaskPrefillTitle(undefined);
          setTaskPrefillDescription(undefined);
          setTaskMeetingMinuteId(undefined);
        }}
      />
    </>
  );
}
